import { useEffect, useMemo } from 'react';

import {
  DialogContent,
  DialogFooter,
} from '@repo/videa-components/src/ui/dialog';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router';

import { ImageType } from '../../../@types/global';
import { selectedVisitAtom } from '../../../app/appHeader/imageViewerMenu/atoms';
import { usePreferences } from '../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

import { usePageContext } from './context/PageContext';
import { TEETH_IMAGE_SETS_VALUES } from './form';
import { useExport } from './hooks/useExport';
import { useFilteredImages } from './hooks/useFilteredImages';
import type { TSummaryTreatment } from './hooks/useSummary';
import { useSummaryImages, useSummaryVisit } from './hooks/useSummary';
import { useTreatmentsToUse } from './hooks/useTreatmentsToUse';
import type { InsuranceView } from './insuranceExport/constants';
import type { TView } from './state';
import {
  clinicalNotesAtom,
  defaultSelectedTeethAtom,
  imageSetsAtom,
  includeFindingTypeAtom,
  insPagesAtom,
  narrativeAtom,
  narrativeDevToolDataAtom,
  patientInfoAtom,
  selectTeethAtom,
  selectedTreatmentCodesAtom,
  selectedTreatmentsAtom,
  teethInImagesAtom,
  useLocalizedFormatDate,
} from './state';
import { SummaryFooter } from './SummaryFooter';
import { SummaryHeader } from './SummaryHeader';
import { SummaryPage } from './SummaryPage';

type ClaimSummaryDialogProps = {
  defaultPatientName: string;
  defaultPatientDOB: string;
  defaultProviderName: string;
  defaultDateOfVisit: string;
  defaultInsurancePages: InsuranceView;
  appointmentVisitId?: number;
  appointmentImagingPatientId?: string;
  appointmentStart?: string;
  appointmentUnifiedId?: string;
  imagingPatientId?: string;
  imagingVisitId?: string;
  appointmentPmsAppointmentId?: string;
  defaultInsuranceView: TView;
  onCloseDialog: () => void;
};

export const ClaimSummaryDialog = ({
  defaultPatientName,
  defaultPatientDOB,
  defaultProviderName,
  defaultDateOfVisit,
  defaultInsurancePages,
  appointmentImagingPatientId,
  appointmentStart,
  appointmentUnifiedId,
  appointmentPmsAppointmentId,
  imagingPatientId,
  imagingVisitId,
  defaultInsuranceView,
  onCloseDialog,
}: ClaimSummaryDialogProps) => {
  // params
  const { id: selectedPatientId } = useParams<{ id: string }>();

  // state
  const setDefaultSelectedTeeth = useSetAtom(defaultSelectedTeethAtom);

  const narrative = useAtomValue(narrativeAtom);
  const narrativeDevToolData = useAtomValue(narrativeDevToolDataAtom);
  const imageSets = useAtomValue(imageSetsAtom);
  const clinicalNotes = useAtomValue(clinicalNotesAtom);
  const selectedVisitId = useAtomValue(selectedVisitAtom);

  const [includeFindingTypes, setIncludeFindingTypes] = useAtom(
    includeFindingTypeAtom,
  );
  const [teethInImages, setTeethInImages] = useAtom(teethInImagesAtom);
  const [patientInfo, setPatientInfo] = useAtom(patientInfoAtom);
  const [selectedTeeth, setSelectedTeeth] = useAtom(selectTeethAtom);
  const [insPage, setInsPage] = useAtom(insPagesAtom);
  const [selectedTreatments, setSelectedTreatments] = useAtom(
    selectedTreatmentsAtom,
  );
  const [selectedTreatmentCodes, setSelectedTreatmentCodes] = useAtom(
    selectedTreatmentCodesAtom,
  );

  // hooks
  const preferences = usePreferences();
  const { currentPage, componentRef } = usePageContext();
  const { trackEvent } = useTrackEvent();

  const { estimatedVisitId, radiographsTakenDate } = useSummaryVisit({
    selectedVisitId,
    selectedPatientId,
    appointmentImagingPatientId: appointmentImagingPatientId ?? null,
    appointmentStart: appointmentStart ?? '',
    insView: defaultInsuranceView,
  });

  const { handleExport } = useExport({
    patientName: patientInfo.name,
    page: currentPage,
    componentRef,
  });

  const {
    images,
    supportedTreatmentType,
    messages,
    treatments,
    isLoading: isLoadingImages,
    dmfValues,
    defaultTeeth: defaultTeethV2,
    findingsPatientName,
    findingsPatientDateOfBirth,
    findingsDateOfService,
    findingsProviderName,
  } = useSummaryImages({
    unifiedAppointmentId: parseInt(appointmentUnifiedId ?? ''),
    defaultEstimatedVisitId: estimatedVisitId?.toString(),
    defaultImagingPatientId: imagingPatientId,
    appointmentImagingPatientId: imagingPatientId,
    appointmentStart: appointmentStart,
    preferences: preferences.data,
  });

  const { treatmentsToUse } = useTreatmentsToUse({
    allTreatments: treatments ?? [],
    selectedTreatmentIds: selectedTreatments ?? [],
  });

  const summaryTreatmentsToUse = treatmentsToUse as TSummaryTreatment[];

  const { selectedTxGroups, isSrp } = useMemo(() => {
    const selectedGroups = Array.from(
      new Set(summaryTreatmentsToUse.map((t) => t.txGroupShortName)),
    );

    return {
      isSrp: selectedGroups.includes('srp') && selectedGroups.length === 1,
      selectedTxGroups: selectedGroups,
    };
  }, [summaryTreatmentsToUse]);

  const { filteredImages } = useFilteredImages({
    images: images ?? [],
    selectedTeeth: selectedTeeth,
    defaultTeeth: defaultTeethV2 ?? [],
    selectedTreatments: summaryTreatmentsToUse,
    selectedTxGroups: selectedTxGroups ?? [],
    imagingVisitId: imagingVisitId ?? '',
    defaultView: defaultInsuranceView,
  });

  const formatDate = useLocalizedFormatDate();

  // vars
  const formattedVisitDate = formatDate(defaultDateOfVisit as string);
  const formattedRadiographsTakenDate = formatDate(radiographsTakenDate);

  // TODO: Move filtered teeth list to atom
  const filteredTeethList = useMemo(() => {
    const filteredTeeth = new Set<string>();

    // Build the list of unique tooth IDs
    filteredImages.forEach((img) =>
      img.teeth.forEach((tooth) => {
        filteredTeeth.add(tooth.toothId);
      }),
    );

    return Array.from(filteredTeeth);
  }, [filteredImages]);

  const allBitewingOnlyTeeth = images.reduce((acc: string[], img) => {
    if (img.imageType === ImageType.BITEWING) {
      img.teeth.forEach((tooth) => {
        if (!acc.includes(tooth.toothId)) {
          acc.push(tooth.toothId);
        }
      });
    }
    return acc;
  }, []);

  // handlers
  const handleSelectTreatments = (txIds: string[]) => {
    setSelectedTreatments(txIds);
    setSelectedTreatmentCodes([
      ...new Set(
        txIds
          .map(
            (txId) =>
              treatments?.find((t) => t.txId.toString() === txId)?.txCode,
          )
          .filter((code): code is string => typeof code === 'string'),
      ),
    ]);
    const defaultTeeth = defaultTeethV2
      ?.filter((tooth) =>
        tooth.treatments.some((tx) => txIds.includes(tx.txId.toString())),
      )
      ?.filter((tooth) => tooth.defaultSelected)
      .map((tooth) => tooth.toothId)
      ?.filter((tooth) =>
        includeFindingTypes.includeBitewingsOnly
          ? allBitewingOnlyTeeth.includes(tooth)
          : tooth,
      );
    setSelectedTeeth(defaultTeeth ?? []);
  };

  const handleSelectBitewingOnly = (isBitewingOnly: boolean) => {
    setIncludeFindingTypes('includeBitewingsOnly', isBitewingOnly);

    const bitewingOnlyTeethWithTreatments = defaultTeethV2
      ?.filter((tooth) =>
        tooth.treatments.some((tx) =>
          selectedTreatments?.includes(tx.txId.toString()),
        ),
      )
      ?.filter((tooth) => tooth.defaultSelected)
      .map((tooth) => tooth.toothId)
      ?.filter((tooth) => allBitewingOnlyTeeth.includes(tooth));

    // If "Include only images for selected teeth" is selected
    if (imageSets === TEETH_IMAGE_SETS_VALUES.Selected) {
      // and "Bitewing type only" is selected
      if (isBitewingOnly) {
        setSelectedTeeth(bitewingOnlyTeethWithTreatments ?? []);
        // and "Bitewing type only" is NOT selected
      } else {
        const defaultTeeth = defaultTeethV2
          ?.filter((tooth) =>
            tooth.treatments.some((tx) =>
              selectedTreatments?.includes(tx.txId.toString()),
            ),
          )
          ?.filter((tooth) => tooth.defaultSelected)
          .map((tooth) => tooth.toothId);

        setSelectedTeeth(defaultTeeth ?? []);
      }
      // If "Include all images" is selected
    } else if (imageSets === TEETH_IMAGE_SETS_VALUES.All) {
      // and "Bitewing type only" is selected
      if (isBitewingOnly) {
        setSelectedTeeth(allBitewingOnlyTeeth);
        // and "Bitewing type only" is NOT selected
      } else {
        setSelectedTeeth(teethInImages);
      }
    }
  };

  // props for child components
  const summaryConfigProps = {
    treatments,
    messages,
    treatmentsToUse: summaryTreatmentsToUse,
    onSelectTreatments: handleSelectTreatments,
  };

  const imagesViewProps = {
    clinicalNotes,
    dmfValues,
    filteredImages,
    imageSetGroup: imageSets,
    narrativeText: narrative,
    narrativeDevToolData,
    patientInfo,
    pmsAppointmentId: appointmentPmsAppointmentId ?? undefined,
    radiographsTakenDateFormatted: formattedRadiographsTakenDate,
    selectedTreatments: summaryTreatmentsToUse.map((t) => ({
      cdtCode: t.txCode,
      quadrant: t.quadrant ?? undefined,
    })),
    supportedTreatmentType: supportedTreatmentType,
    visitDate: formattedVisitDate,
    isPaginated:
      preferences?.data?.insuranceExportPreviewPages !== 'single' &&
      insPage === 'preview-pages',
  };

  // useEffects
  useEffect(() => {
    setInsPage(defaultInsurancePages);
  }, [defaultInsurancePages, setInsPage]);

  // Prefer patient data from findings endpoint when available (opened from InsuranceClaimFooter)
  useEffect(() => {
    const name =
      findingsPatientName != null && findingsPatientName !== ''
        ? findingsPatientName
        : defaultPatientName;
    const dob =
      findingsPatientDateOfBirth != null && findingsPatientDateOfBirth !== ''
        ? formatDate(findingsPatientDateOfBirth)
        : defaultPatientDOB;
    const dateOfVisit =
      findingsDateOfService != null && findingsDateOfService !== ''
        ? formatDate(findingsDateOfService)
        : defaultDateOfVisit;
    const providerName =
      findingsProviderName != null && findingsProviderName !== ''
        ? findingsProviderName
        : defaultProviderName;

    setPatientInfo({
      name,
      dob,
      dateOfVisit,
      radiographsTakenDate: formattedRadiographsTakenDate,
      providerName,
    });
    // formatDate is stable for our purposes; omit to avoid re-running every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultPatientName,
    defaultPatientDOB,
    defaultDateOfVisit,
    formattedRadiographsTakenDate,
    setPatientInfo,
    defaultProviderName,
    findingsPatientName,
    findingsPatientDateOfBirth,
    findingsDateOfService,
    findingsProviderName,
  ]);

  useEffect(() => {
    if (treatments.length) {
      setSelectedTreatments(treatments.map((t) => t.txId.toString()));
      setSelectedTreatmentCodes(treatments.map((t) => t.txCode));
    }
  }, [treatments, setSelectedTreatments, setSelectedTreatmentCodes]);

  useEffect(() => {
    const defaultTeeth = defaultTeethV2
      ?.filter((t) => t.defaultSelected)
      .map((t) => t.toothId);

    setSelectedTeeth(defaultTeeth ?? []);
    setDefaultSelectedTeeth(defaultTeeth ?? []);
    setTeethInImages(defaultTeethV2?.map((t) => t.toothId) ?? []);
  }, [
    defaultTeethV2,
    setSelectedTeeth,
    setDefaultSelectedTeeth,
    setTeethInImages,
  ]);

  return (
    <DialogContent
      className="vai-w-[1250px] vai-max-w-[1250px] vai-max-h-[90vh] vai-flex vai-flex-col"
      aria-describedby={undefined}
      data-testid="claim-summary-dialog"
    >
      <SummaryHeader insView={insPage} />

      <div className="vai-flex vai-flex-col vai-overflow-y-auto vai-max-h-[70vh]">
        <div className="vai-flex vai-flex-col">
          <SummaryPage
            filteredTeethList={filteredTeethList}
            defaultTeethList={
              includeFindingTypes.includeBitewingsOnly
                ? teethInImages.filter((tooth) =>
                    allBitewingOnlyTeeth.includes(tooth),
                  )
                : teethInImages
            }
            images={images}
            insPage={insPage}
            estimatedVisitId={estimatedVisitId}
            unifiedAppointmentId={
              appointmentUnifiedId ? parseInt(appointmentUnifiedId) : undefined
            }
            includeFindingTypes={includeFindingTypes}
            isLoading={isLoadingImages}
            isSrp={isSrp}
            onSelectBitewingOnly={handleSelectBitewingOnly}
            {...imagesViewProps}
            {...summaryConfigProps}
          />
        </div>
      </div>

      <DialogFooter>
        {!isLoadingImages && (
          <SummaryFooter
            defaultInsuranceView={defaultInsuranceView}
            defaultInsurancePages={defaultInsurancePages}
            insPage={insPage}
            numImages={filteredImages?.length}
            onCloseDialog={onCloseDialog}
            onExport={(e) => {
              handleExport(e, 'jpeg');
              selectedTreatmentCodes?.length &&
                trackEvent(mixpanelEvents.downloadFreeAttachment, {
                  unifiedId: appointmentUnifiedId ?? '',
                  estimatedVisitId,
                  entryPoint: 'Clinical Viewer',
                  txCode: selectedTreatmentCodes,
                  attachmentType:
                    preferences?.data.insuranceExportPreviewPages === 'single'
                      ? 'Digital'
                      : 'Print',
                });
            }}
            onSetInsPage={(pg: InsuranceView) => setInsPage(pg)}
          />
        )}
      </DialogFooter>
    </DialogContent>
  );
};
